/**
 * All of the configurator's state, in one store.
 *
 * Two halves, kept apart on purpose:
 *
 * - **`spec`** is the product (DESIGN §2). It is exactly the object the engine
 *   takes, nothing more, and it is what would be stored and handed to the paid-PDF
 *   generator. Nothing about how the preview looks may leak into it.
 * - **`view`** is how this browser is currently looking at the design: which layers
 *   are shown, how hard the renderer is working, what color the shopper is
 *   imagining the siding. None of it is bought, and none of it reaches the engine.
 *
 * Only the spec outlives the page. It is kept in this browser on every change and is
 * what the store opens on (`state/saved.ts`), so a reload or a canceled checkout comes
 * back to the shopper's own shed. The view is how this visit is looking, and starts
 * fresh.
 */

import type {
  Assembly,
  Foundation,
  Opening,
  Siding,
  Sixteenths,
  Spec,
  WallName,
} from "@planforge/wasm";
import { create } from "zustand";

import { moveToWall, placeOpening, refitOpenings } from "../engine/openings.js";
import { defaultSpec } from "../engine/spec.js";
import type { Preset } from "../presets/catalog.js";
import { openDesign, saveDesign } from "./saved.js";
import { ASSEMBLIES, LAYERS, type LayerId } from "../three/layers.js";

/** How much the renderer is allowed to spend per frame. */
export type Quality = "low" | "balanced" | "high";

export interface ViewState {
  assemblies: Record<Assembly, boolean>;
  layers: Record<LayerId, boolean>;
  quality: Quality;
  depthOfField: boolean;
  /** The grass. Off is how you get under the building to look at the floor frame. */
  ground: boolean;
  /**
   * Take away whatever stands between the camera and the inside, as it orbits
   * (`scene/cutaway.ts`). A rule about the view, applied on top of the switches: a
   * wall switched off stays off, and a wall switched on can still be cut.
   */
  cutaway: boolean;
  /** Preview-only finishes. The plan sells framing; paint is the buyer's choice. */
  sidingColor: string;
  roofColor: string;
}

/**
 * What replaced a design that can be taken back: Reset, or a ready-made design opened
 * from a link (`presets/open.ts`), or a design sent from another screen (`share/link.ts`).
 * The Undo row says which, by name.
 */
export type Replacement =
  | { kind: "reset" }
  | { kind: "preset"; name: string }
  | { kind: "link" };

interface Store {
  spec: Spec;
  view: ViewState;
  /** Bumped by `frame()`; the camera rig watches it and re-fits the model. */
  frameRequest: number;
  /**
   * What the cutaway has taken away at this moment. Written by the viewport when the
   * camera crosses a threshold, never by a control — it is a fact about the camera,
   * and the panel reads it only to say which walls are missing and why.
   */
  cutAway: ReadonlySet<Assembly>;
  /**
   * What the last Reset — or the last ready-made design opened over the shopper's own —
   * threw away, until it is undone or the design moves on.
   *
   * Those are the two ways left to lose a design outright, since the design survives a
   * reload: Reset sits in the footer beside the buttons a shopper clicks most, and a
   * "Customize this" link followed from a `/sheds/` page opens its design over
   * whatever this browser was working on. So either can be taken back. Any other change
   * to the design clears it: an undo after the shopper has started on the new design
   * would throw *that* away instead.
   */
  discarded: { spec: Spec; view: ViewState; by: Replacement } | undefined;
  /**
   * The openings as the shopper last placed them, which is not always where they stand.
   *
   * A change to the shed slides an opening back inside a wall that got shorter or lower
   * (`refitOpenings`), and a slider fires that change at every step of a drag. Refitting
   * from where the last step left it would keep each step's clamp, so dragging Width
   * down and back up would leave the door where the narrowest width put it. So a refit
   * always starts from here, and only the shopper's own edits move it: adding, sliding,
   * moving or removing an opening moves that opening's entry and no other; Reset, its
   * Undo and going back to a design replace the lot. Nothing else does — not the shed's
   * size, not its roof, siding or foundation. Not part of the spec, and not kept across a reload — it is how this
   * visit has been placing the openings, not something bought.
   */
  chosen: Spec["openings"];

  /** Replace the whole design — how the configurator goes back to the shed on screen. */
  setSpec(spec: Spec): void;
  patchSpec(patch: Partial<Spec>): void;
  patchRoof(patch: Partial<Spec["roof"]>): void;
  patchFraming(patch: Partial<Spec["framing"]>): void;
  patchFootprint(patch: Partial<Spec["footprint"]>): void;
  setFoundation(foundation: Foundation): void;
  setSiding(siding: Siding): void;
  /** The longest board the cut list buys: the building does not move, only the load. */
  setHaulLength(haulLength: Sixteenths): void;

  /**
   * A new door or window, in free wall (`placeOpening`). Nothing happens when no wall
   * has room — the button says so before it is pressed — so a click never adds an error.
   */
  addOpening(type: Opening["type"]): void;
  patchOpening(id: string, patch: Partial<Opening>): void;
  /** Onto another wall, into that wall's free run nearest where it stood (`moveToWall`). */
  moveOpening(id: string, wall: WallName): void;
  removeOpening(id: string): void;

  toggleAssembly(assembly: Assembly): void;
  toggleLayer(layer: LayerId): void;
  setView(patch: Partial<ViewState>): void;
  setCutAway(cut: ReadonlySet<Assembly>): void;
  showEverything(): void;
  showFramingOnly(): void;
  frame(): void;
  /** Back to the default shed and a fresh view, keeping what it replaced in `discarded`. */
  reset(): void;
  /**
   * Opens a ready-made design in place of the one in the controls, keeping what it
   * replaced in `discarded` unless that was the default shed untouched. Opening the
   * design already on screen does nothing, so a link followed twice does not offer the
   * same shed back as an undo.
   */
  openPreset(preset: Preset): void;
  /**
   * Opens a design a link carried (`share/link.ts`), the same way: kept for Undo, and
   * nothing at all when it is the design already on screen.
   */
  openLinked(spec: Spec): void;
  /** Puts back what the last Reset or preset replaced. Nothing, if there is nothing to put back. */
  undoReset(): void;
}

const allOn = <T extends string>(keys: readonly T[]): Record<T, boolean> =>
  Object.fromEntries(keys.map((key) => [key, true])) as Record<T, boolean>;

export function defaultView(): ViewState {
  return {
    assemblies: allOn(ASSEMBLIES),
    layers: allOn(LAYERS),
    quality: "balanced",
    depthOfField: false,
    ground: true,
    cutaway: false,
    sidingColor: "#c8c2b4",
    roofColor: "#9aa0a6",
  };
}

/**
 * A change to the design, as the store records one: the new spec, and the end of any
 * Reset still waiting to be undone. Where the shopper chose to put the openings is left
 * as it was — a siding or a foundation moves no door — so a shed shrunk, then
 * re-sided, then grown back still puts its openings back.
 */
function designed(spec: Spec): Pick<Store, "spec" | "discarded"> {
  return { spec, discarded: undefined };
}

/** A whole design replacing the one in the controls: its openings are where it put them. */
function replaced(spec: Spec): Pick<Store, "spec" | "discarded" | "chosen"> {
  return { ...designed(spec), chosen: spec.openings };
}

/**
 * The shopper placing one opening by hand: that one entry of `chosen` becomes where it now
 * stands, and every other opening keeps where the shopper put it, not where the last refit
 * left it. `placed` undefined is the opening removed.
 */
function placedBy(
  chosen: Spec["openings"],
  id: string,
  placed: Spec["openings"][number] | undefined,
): Spec["openings"] {
  const others = chosen.filter((opening) => opening.id !== id);
  if (placed === undefined) {
    return others;
  }
  return chosen.some((opening) => opening.id === id)
    ? chosen.map((opening) => (opening.id === id ? placed : opening))
    : [...others, placed];
}

/**
 * A change to the shed itself — its size, its walls, its framing, its roof — with the openings
 * kept on the walls it now has (`refitOpenings`). Only a change that can move a wall's
 * ends or its plate goes through here; an opening's own controls never do, or dragging
 * a door into a corner would be quietly undone under the shopper's finger.
 *
 * Each opening is refitted from where the shopper chose to put it (`chosen`), not from
 * where the last change left it, so a shed made smaller and then bigger again puts its
 * openings back. `chosen` is left as it was: this is not the shopper moving a door.
 */
function reshaped(spec: Spec, chosen: Spec["openings"]): Pick<Store, "spec" | "discarded"> {
  const placed = new Map(chosen.map((opening) => [opening.id, opening]));
  const asChosen = spec.openings.map((opening) => {
    const at = placed.get(opening.id);
    return at === undefined ||
      at.wall !== opening.wall ||
      (at.offset === opening.offset && at.sill === opening.sill)
      ? opening
      : { ...opening, offset: at.offset, sill: at.sill };
  });
  const restored = asChosen.some((opening, index) => opening !== spec.openings[index])
    ? { ...spec, openings: asChosen }
    : spec;
  const openings = refitOpenings(restored);
  return {
    spec: openings === restored.openings ? restored : { ...restored, openings },
    discarded: undefined,
  };
}

const sameSpec = (a: Spec, b: Spec): boolean => JSON.stringify(a) === JSON.stringify(b);

/** The design this browser was last working on, or the default shed. */
const opened = openDesign();

/**
 * Whether the configurator opened on the design this browser was last working on —
 * including the default shed, when that is what the shopper left it on. Read once, when
 * the store is made, for the screens that tell a returning shopper their design is
 * still here, which they may only say when it is (`OpenedDesign` in `saved.ts`).
 */
export const openedOnLastDesign = opened.isLastDesign;

export const useStore = create<Store>((set) => ({
  spec: opened.spec,
  view: defaultView(),
  frameRequest: 0,
  cutAway: new Set(),
  discarded: undefined,
  chosen: opened.spec.openings,

  setSpec: (spec) => set(replaced(spec)),
  patchSpec: (patch) => set((state) => reshaped({ ...state.spec, ...patch }, state.chosen)),
  // The roof moves a plate: a lean-to's high wall stands taller by the roof's climb, so a
  // shallower pitch, or a change of style, lowers it under the openings in it.
  patchRoof: (patch) =>
    set((state) =>
      reshaped({ ...state.spec, roof: { ...state.spec.roof, ...patch } }, state.chosen),
    ),
  patchFraming: (patch) =>
    set((state) =>
      reshaped({ ...state.spec, framing: { ...state.spec.framing, ...patch } }, state.chosen),
    ),
  patchFootprint: (patch) =>
    set((state) =>
      reshaped({ ...state.spec, footprint: { ...state.spec.footprint, ...patch } }, state.chosen),
    ),
  // A slab's floor is at the ground, so its doors have no ramp: dropped with the choice
  // that made them meaningless, as a hand is when a door goes back to prehung, rather
  // than left for the engine to warn about something the shopper did not do.
  setFoundation: (foundation) =>
    set((state) =>
      designed({
        ...state.spec,
        foundation,
        openings:
          foundation === "slab" && state.spec.openings.some((opening) => opening.ramp === true)
            ? state.spec.openings.map(({ ramp: _ramp, ...opening }) => opening)
            : state.spec.openings,
      }),
    ),
  setSiding: (siding) => set((state) => designed({ ...state.spec, siding })),
  setHaulLength: (haulLength) => set((state) => designed({ ...state.spec, haulLength })),

  addOpening: (type) =>
    set((state) => {
      const opening = placeOpening(state.spec, type);
      return opening === undefined
        ? {}
        : {
            ...designed({ ...state.spec, openings: [...state.spec.openings, opening] }),
            chosen: placedBy(state.chosen, opening.id, opening),
          };
    }),

  patchOpening: (id, patch) =>
    set((state) => {
      const current = state.spec.openings.find((opening) => opening.id === id);
      if (current === undefined) {
        return {};
      }
      const patched = { ...current, ...patch };
      // Only what the shopper set is where they put it: a door made wider on a small
      // shed keeps the place it was chosen at, not the place the refit had slid it to.
      const before = state.chosen.find((opening) => opening.id === id);
      const placed =
        before === undefined || before.wall !== patched.wall
          ? patched
          : { ...patched, offset: patch.offset ?? before.offset, sill: patch.sill ?? before.sill };
      return {
        ...designed({
          ...state.spec,
          openings: state.spec.openings.map((opening) => (opening.id === id ? patched : opening)),
        }),
        chosen: placedBy(state.chosen, id, placed),
      };
    }),

  moveOpening: (id, wall) =>
    set((state) => {
      const moved = moveToWall(state.spec, id, wall);
      return moved === undefined
        ? {}
        : {
            ...designed({
              ...state.spec,
              openings: state.spec.openings.map((opening) => (opening.id === id ? moved : opening)),
            }),
            chosen: placedBy(state.chosen, id, moved),
          };
    }),

  removeOpening: (id) =>
    set((state) => ({
      ...designed({
        ...state.spec,
        openings: state.spec.openings.filter((opening) => opening.id !== id),
      }),
      chosen: placedBy(state.chosen, id, undefined),
    })),

  toggleAssembly: (assembly) =>
    set((state) => ({
      view: {
        ...state.view,
        assemblies: {
          ...state.view.assemblies,
          [assembly]: !state.view.assemblies[assembly],
        },
      },
    })),

  toggleLayer: (layer) =>
    set((state) => ({
      view: {
        ...state.view,
        layers: { ...state.view.layers, [layer]: !state.view.layers[layer] },
      },
    })),

  setView: (patch) => set((state) => ({ view: { ...state.view, ...patch } })),
  setCutAway: (cutAway) => set({ cutAway }),

  showEverything: () =>
    set((state) => ({
      view: { ...state.view, assemblies: allOn(ASSEMBLIES), layers: allOn(LAYERS) },
    })),

  // What a builder wants to look at: the skeleton, with the skin taken off.
  showFramingOnly: () =>
    set((state) => ({
      view: {
        ...state.view,
        assemblies: allOn(ASSEMBLIES),
        layers: {
          framing: true,
          sheathing: false,
          siding: false,
          roofing: false,
          fixtures: false,
        },
      },
    })),

  frame: () => set((state) => ({ frameRequest: state.frameRequest + 1 })),
  reset: () =>
    set((state) => {
      const fresh = defaultSpec();
      return {
        spec: fresh,
        chosen: fresh.openings,
        view: defaultView(),
        // Resetting the default shed throws nothing away, so there is nothing to undo.
        discarded: sameSpec(state.spec, fresh)
          ? undefined
          : { spec: state.spec, view: state.view, by: { kind: "reset" } },
      };
    }),
  openPreset: (preset) =>
    set((state) =>
      sameSpec(state.spec, preset.spec)
        ? {}
        : {
            ...replaced(preset.spec),
            // The default shed untouched is nothing to lose — a first visit from a
            // search result — and Reset is the way back to it anyway.
            discarded: sameSpec(state.spec, defaultSpec())
              ? undefined
              : { spec: state.spec, view: state.view, by: { kind: "preset", name: preset.name } },
            // A different size of shed: the camera was fitted to the last one.
            frameRequest: state.frameRequest + 1,
          },
    ),
  openLinked: (spec) =>
    set((state) =>
      sameSpec(state.spec, spec)
        ? {}
        : {
            ...replaced(spec),
            discarded: sameSpec(state.spec, defaultSpec())
              ? undefined
              : { spec: state.spec, view: state.view, by: { kind: "link" } },
            frameRequest: state.frameRequest + 1,
          },
    ),
  undoReset: () =>
    set((state) =>
      state.discarded === undefined
        ? {}
        : {
            spec: state.discarded.spec,
            chosen: state.discarded.spec.openings,
            view: state.discarded.view,
            discarded: undefined,
            frameRequest:
              state.discarded.by.kind === "reset" ? state.frameRequest : state.frameRequest + 1,
          },
    ),
}));

// Every change, as it happens (`saveDesign` says why not on a timer). A browser that
// will not store anything still designs; it just starts over on the next page.
useStore.subscribe((state, previous) => {
  if (state.spec !== previous.spec) {
    saveDesign(state.spec);
  }
});
